/** @jsx jsx */
import React, { useRef, useLayoutEffect, useEffect } from 'react'
import styled from '@emotion/styled'
import { jsx } from '@emotion/react'
import mq from '../styles/mediaQueries'
import { useMediaQuery } from 'react-responsive'
import * as basicScroll from 'basicscroll'
import { GatsbyImage as Image } from 'gatsby-plugin-image'
import { Link } from 'gatsby'

import { Body1, Body2 } from './Typography'
import breakPoints from '../styles/breakPoints'

const ParallaxContainer = styled.div(props =>
  mq({
    position: 'relative',
    display: 'flex',
    minHeight: ['55vw', '55vw', '45vw', '45vw', '45vw', '45vw'],
    justifyContent: 'space-between',
    marginBottom: '160px',
    flexDirection:
      props.position === 'left'
        ? ['row', 'row-reverse', 'row-reverse', 'row-reverse', 'row-reverse']
        : ['row-reverse', 'row', 'row', 'row', 'row'],
    WebkitTransform: 'rotateZ(360deg)',
  })
)

const ParallaxImage = styled.div`
  will-change: transform;
  transform: translateY(var(--translateY));
`

const ParallaxText = styled.div`
  transform: translateY(var(--translateY));
  will-change: transform;
  ${mq({
    display: ['none', 'block', 'block', 'block', 'block'],
  })};
`

const FloatingImage = styled(Image)`
  ${mq({
    height: ['50vw', '30vw', '30vw', '30vw', '30vw'],
    minWidth: ['50vw', '30vw', '30vw', '30vw', '30vw'],
  })};
  filter: drop-shadow(0px 60px 80px rgba(0, 0, 0, 0.2))
    drop-shadow(0px 24px 36px rgba(0, 0, 0, 0.16))
    drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.12));
`

const BackgroundImageContainer = styled.div(props =>
  mq({
    left:
      props.position === 'left'
        ? ['unset', '5vw', '7.5vw', '15vw', '15vw']
        : ['0', 'unset', 'unset', 'unset', 'unset'],
    right:
      props.position === 'right'
        ? ['unset', '5vw', '7.5vw', '15vw', '15vw']
        : ['0', 'unset', 'unset', 'unset', 'unset'],
    height: ['80vw', '55vw', '45vw', '45vw', '45vw'],
    minWidth: ['80vw', '55vw', '45vw', '45vw', '45vw'],
    position: ['absolute', 'absolute', 'absolute', 'absolute', 'absolute'],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background:
      'linear-gradient(90.9deg, #2e60ff 0%, rgba(46, 96, 255, 0) 99.36%), #0000fe',
    filter:
      'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.12)) drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08)) drop-shadow(0px 2px 24px rgba(0, 0, 0, 0.04))',
  })
)

const BrandHeader = styled(Image)`
  height: 80%;
  width: 80%;
`

const BrandLogoContainer = styled.div(props =>
  mq({
    display: 'flex',
    marginBottom: ['20.5px', '20.5px', '38px', '38px', '38px'],
    justifyContent:
      props.position === 'left'
        ? ['flex-start', 'flex-end', 'flex-end', 'flex-end', 'flex-end']
        : ['flex-end', 'flex-start', 'flex-start', 'flex-start', 'flex-start'],
  })
)

const TextOuter = styled.div(props =>
  mq(props.isSmall ? Body2 : Body1, {
    width: ['unset', '60vw', '50vw', '40vw', '622px'],
    position: 'relative',
    textAlign:
      props.position === 'left'
        ? ['left', 'right', 'right', 'right', 'right']
        : ['right', 'left', 'left', 'left', 'left'],
  })
)

const TextLeft = styled.div`
  ${mq({
    position: ['relative', 'absolute', 'absolute', 'absolute', 'absolute'],
  })};
  left: 0;
  clip-path: inset(0 0 0 0);
`

const TextRight = styled.div`
  position: absolute;
  left: 0;
  color: white;
  ${mq({
    display: ['none', 'block', 'block', 'block', 'block'],
  })};
`

const CaseStudyLink = styled(Link)(props =>
  mq(props.isSmall ? Body2 : Body1, {
    fontWeight: 'bold',
    color: 'var(--firewall-500)',
    textDecoration: 'none',
    position: 'relative',
    display: 'flex',
    justifyContent:
      props.position === 'left'
        ? ['flex-start', 'flex-end', 'flex-end', 'flex-end', 'flex-end']
        : ['flex-end', 'flex-start', 'flex-start', 'flex-start', 'flex-start'],
  })
)

const Spacer = styled.div`
  ${mq({
    display: ['none', 'block', 'block', 'block', 'block'],
  })};
`

const TextContainer = styled.div`
  ${mq({
    margin: ['0 20px 40px 20px', '0', '0', '0', '0'],
  })};
`

const MobileTextContainer = styled.div`
  ${mq({
    margin: ['0 20px 40px 20px', '0', '0', '0', '0'],
    display: ['block', 'none', 'none', 'none', 'none'],
  })};
`

const ParallaxComponent = ({
  position,
  background,
  image,
  text,
  logo,
  path,
}) => {
  const isMobile = useMediaQuery({ query: `(max-width: ${breakPoints.sm}px)` })
  const isSmall = useMediaQuery({ query: `(max-width: ${breakPoints.md}px)` })

  const parallaxTextRef = useRef()
  const parallaxImageRef = useRef()
  const backgroundRef = useRef()
  const textRef = useRef()
  const rightTextRef = useRef()
  const spacerRef = useRef()

  function calculateOverlap() {
    const left = backgroundRef.current.getBoundingClientRect().left
    const width = backgroundRef.current.offsetWidth
    const right = left + width
    const leftText = textRef.current.getBoundingClientRect().left
    const widthText = textRef.current.offsetWidth
    const textHeight = textRef.current.clientHeight

    spacerRef.current.style.height = `${textHeight}px`

    if (position === 'left') {
      const textOverlapPercentage = 100 - ((right - leftText) / widthText) * 100
      rightTextRef.current.style.clipPath = `inset(0 ${textOverlapPercentage}% 0 0)`
    }

    if (position === 'right') {
      const textOverlapPercentage = ((left - leftText) / widthText) * 100
      rightTextRef.current.style.clipPath = `inset(0 0 0 ${textOverlapPercentage}%)`
    }
  }

  useLayoutEffect(() => {
    if (!isMobile) {
      requestAnimationFrame(calculateOverlap)
    }

    const parallaxImageScene = parallaxImageRef.current
    const parallaxTextScene = parallaxTextRef.current

    basicScroll
      .create({
        elem: parallaxTextScene,
        from: 'top-bottom',
        to: 'bottom-top',
        direct: true,
        props: {
          '--translateY': {
            from: '10%',
            to: '50%',
          },
        },
      })
      .start()

    basicScroll
      .create({
        elem: parallaxImageScene,
        from: 'top-bottom',
        to: 'bottom-top',
        direct: true,
        props: {
          '--translateY': {
            from: '70%',
            to: '-20%',
          },
        },
      })
      .start()
  }, [])

  useEffect(() => {
    if (!isMobile) {
      window.addEventListener('resize', calculateOverlap)

      return () => {
        window.removeEventListener('resize', calculateOverlap)
      }
    }
  }, [isMobile])

  return (
    <React.Fragment>
      <MobileTextContainer>
        <BrandLogoContainer position={position}>{logo}</BrandLogoContainer>
        <TextOuter position={position} small={isSmall ? 1 : undefined}>
          {text}
        </TextOuter>
        <CaseStudyLink
          position={position}
          small={isSmall ? 1 : undefined}
          to={path}
        >
          Case study →
        </CaseStudyLink>
      </MobileTextContainer>
      <ParallaxContainer position={position}>
        <BackgroundImageContainer position={position} ref={backgroundRef}>
          <BrandHeader image={background.gatsbyImageData} />
        </BackgroundImageContainer>
        <ParallaxText ref={parallaxTextRef}>
          <TextContainer>
            <BrandLogoContainer position={position}>{logo}</BrandLogoContainer>
            <TextOuter small={isSmall ? 1 : undefined} position={position}>
              <TextLeft ref={textRef}>{text}</TextLeft>
              <TextRight ref={rightTextRef}>{text}</TextRight>
            </TextOuter>
            <Spacer ref={spacerRef} />
            <CaseStudyLink
              position={position}
              small={isSmall ? 1 : undefined}
              to={path}
            >
              Case study →
            </CaseStudyLink>
          </TextContainer>
        </ParallaxText>
        <ParallaxImage ref={parallaxImageRef}>
          <FloatingImage image={image.gatsbyImageData} />
        </ParallaxImage>
      </ParallaxContainer>
    </React.Fragment>
  )
}

export default ParallaxComponent
